import { Observable, Subject } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import {
  SnTag,
  SnCamera,
  SnLocation,
  SnCameraDisposition,
  SnCameraUnmanagedType,
  SnCameraManagementType,
  SnCameraStatusHistoricalList,
} from '@lib/api/entities';
import { SnSharesAPI, SnCamerasAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { SnLocationFieldsGeneratorService } from '@lib/locations';
import { SnShareUiService } from '@lib/share';
import { SnTagFieldGeneratorService } from '@lib/tags';
import { t } from '@lib/translation';
import { SnDrawerRef, SnDrawerService, SnDrawerConfig } from '@lib/ui/drawer';
import { SnFilterService } from '@lib/ui/filter';
import { SnMenuItemConfig } from '@lib/ui/menu';

import { CameraFormDrawerComponent } from '../components/camera-form-drawer/camera-form-drawer.component';
import { PlaceCamerasDialogComponent } from '../components/place-cameras-dialog/place-cameras-dialog.component';
import { SnCameraDetailsDrawerContainer } from '../containers/camera-details-drawer/camera-details-drawer.container';
import { SnCameraFieldsGeneratorService } from './camera-fields-generator.service';

interface ICameraViewData {
  camera: SnCamera;
  actions: SnMenuItemConfig<SnCamera>[];
}

@Injectable()
export class CameraActionsService implements OnDestroy {
  public readonly canAdd: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;

  public readonly canShare: boolean;
  public readonly canDeleteShare: boolean;

  private _viewDrawer: SnDrawerRef<SnCameraDetailsDrawerContainer, ICameraViewData, SnCamera>;
  private _formDrawer: SnDrawerRef<CameraFormDrawerComponent, SnCamera, SnCamera>;
  private _filtersDrawer: SnDrawerRef;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _sharesAPI: SnSharesAPI,
    private readonly _shareUiService: SnShareUiService,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _drawer: SnDrawerService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _tagFieldsGenerator: SnTagFieldGeneratorService,
    private readonly _locationFieldsGenerator: SnLocationFieldsGeneratorService,
    private readonly _cameraFieldsGeneratorService: SnCameraFieldsGeneratorService,
    private readonly _filterService: SnFilterService,
    private readonly _matDialog: MatDialog,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'cameras/camera', action: 'add' });
    this.canChange = ability.can({ subject: 'cameras/camera', action: 'change' });
    this.canDelete = ability.can({ subject: 'cameras/camera', action: 'delete' });

    this.canShare = ability.can({ subject: 'sharing/share', action: ['add', 'change'] });
    this.canDeleteShare = ability.can({ subject: 'sharing/share', action: 'delete' });
  }

  public ngOnDestroy(): void {
    if (this._viewDrawer) {
      this._viewDrawer.close();
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  public onTagSelected(tag: SnTag): void {
    this._router.navigate([], {
      queryParams: { tags: tag.id },
      replaceUrl: true,
      relativeTo: this._activatedRoute,
    });
  }

  public view(config: SnDrawerConfig): Observable<SnCamera> {
    if (this._viewDrawer) {
      if (this._viewDrawer.data.camera.id !== config.data.id) {
        this._viewDrawer.data = config.data;
      }
    } else {
      this._viewDrawer = this._drawer.open<SnCameraDetailsDrawerContainer, ICameraViewData>(
        SnCameraDetailsDrawerContainer,
        {
          width: 320,
          hasBackdrop: false,
          ...config,
        },
      );

      this._viewDrawer.afterClosed$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this._viewDrawer = null;
      });
    }

    return this._viewDrawer.dataChange$.pipe(
      map(({ camera }) => camera),
      takeUntil(this._destroy$),
    );
  }

  public viewRecordings(camera: SnCamera): void {
    const url = this._router.serializeUrl(
      this._router.createUrlTree([`/recordings-panel/${camera.id}`]),
    );

    window.open(url, '_blank');
  }

  public edit(camera: SnCamera, config?: SnDrawerConfig<SnCamera>): Observable<SnCamera> {
    if (!this.canChange) {
      return;
    }

    return this._createFormDrawer({
      ...config,
      data: camera,
    });
  }

  public clone(camera: SnCamera, config?: SnDrawerConfig<SnCamera>): Observable<SnCamera> {
    const cameraClone = camera.clone();
    cameraClone.name = `Copy ${camera.name}`;
    cameraClone.id = null;

    return this._createFormDrawer({
      ...config,
      data: cameraClone,
    });
  }

  public create(location?: SnLocation, config?: SnDrawerConfig<SnCamera>): Observable<SnCamera> {
    const geometry = location ? location.geometry : this._currentOrganization.settings.geometry;
    const camera = new SnCamera({ geometry, location });

    return this._createFormDrawer({
      ...config,
      data: camera,
    });
  }

  public createDescendant(
    parent: SnCamera,
    config?: SnDrawerConfig<SnCamera>,
  ): Observable<SnCamera> {
    const geometry = parent.location
      ? parent.location?.geometry
      : this._currentOrganization.settings.geometry;
    const camera = new SnCamera({ geometry, location: parent.location, parentCamera: parent.id });
    return this._createFormDrawer({
      ...config,
      data: camera,
    });
  }

  public delete(camera: SnCamera): Observable<Object> {
    if (camera.parentCamera) {
      return this._camerasAPI
        .deleteDescendant(camera.parentCamera, camera.id)
        .pipe(takeUntil(this._destroy$));
    }
    const delete$ = camera.isOwned
      ? this._camerasAPI.deleteCamera(camera.id)
      : this._sharesAPI.deleteShare(camera.shareInfo.id);

    return delete$.pipe(takeUntil(this._destroy$));
  }

  public cameraHistory(camera: SnCamera): Observable<SnCameraStatusHistoricalList> {
    return this._camerasAPI.getCameraStatusHistory(camera.id);
  }

  public share(camera: SnCamera): Observable<SnCamera> {
    return this._shareUiService
      .shareSettings({
        object: camera,
        handlers: this._camerasAPI,
      })
      .afterClosed$.pipe(
        concatMap(() => this._camerasAPI.getCamera(camera.id)),
        takeUntil(this._destroy$),
      );
  }

  public search(value: string): void {
    this._updateQueryParams({ search: value });
  }

  public openFilters(isOwned: boolean, selectedFields?: string[]): void {
    const fields = selectedFields?.length
      ? this._generateFilterFields(isOwned, selectedFields)
      : this._generateFilterFields(isOwned);
    this._filtersDrawer = this._filterService.createFilter({ data: fields });

    this._filtersDrawer.afterClosed$.pipe(takeUntil(this._destroy$)).subscribe((params) => {
      if (params) {
        this._updateQueryParams(params);
      }

      this._filtersDrawer = undefined;
    });
  }

  public place(location: SnLocation): void {
    this._matDialog.open(PlaceCamerasDialogComponent, {
      data: location,
      hasBackdrop: true,
      minWidth: '75vw',
    });
  }

  private _createFormDrawer(config: SnDrawerConfig<SnCamera>): Observable<SnCamera> {
    if (this._formDrawer) {
      return;
    }

    this._formDrawer = this._drawer.open<CameraFormDrawerComponent, SnCamera, SnCamera>(
      CameraFormDrawerComponent,
      {
        hasBackdrop: true,
        width: 320,
        ...config,
      },
    );

    this._formDrawer.afterClosed$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => (this._formDrawer = null));

    return this._formDrawer.afterClosed$;
  }

  private _updateQueryParams(params: Params): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: params,
    });
  }

  private _generateFilterFields(isOwned: boolean, fields?: string[]): SnFormFieldConfig[] {
    const params: any = {
      isOwned,
    };

    const formFields = [
      {
        key: 'disposition',
        type: 'select',
        templateOptions: {
          label: t('common.disposition'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('common.external'), value: SnCameraDisposition.External },
            { label: t('common.internal'), value: SnCameraDisposition.Internal },
          ],
        },
      },
      {
        key: 'status',
        type: 'select',
        templateOptions: {
          label: t('cameras.filters.liveStatus'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('common.up'), value: 'Up' },
            { label: t('common.down'), value: 'Down' },
            { label: t('common.pending'), value: 'Pending' },
            { label: t('common.unconfigured'), value: 'Unconfigured' },
            { label: t('common.unmanaged'), value: 'Unmanaged' },
          ],
        },
      },
      {
        key: 'managementType',
        type: 'select',
        templateOptions: {
          label: t('cameras.filters.streamType'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('cameras.managementType.fususCore'), value: SnCameraManagementType.Managed },
            { label: t('common.directConnect'), value: SnCameraManagementType.Unmanaged },
          ],
        },
      },
      this._cameraFieldsGeneratorService.getHostField({ key: 'host_Icontains' }),
      {
        key: 'unmanagedType',
        type: 'select',
        templateOptions: {
          label: t('cameras.filters.directConnectType'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('cameras.unmanagedType.jpeg'), value: SnCameraUnmanagedType.JPEG },
            { label: t('cameras.unmanagedType.hls'), value: SnCameraUnmanagedType.HLS },
            { label: t('cameras.unmanagedType.sldp'), value: SnCameraUnmanagedType.SLDP },
          ],
        },
      },
      this._locationFieldsGenerator.getLocationSelectField(
        { key: 'location' },
        { ordering: 'name', ...params },
      ),
      this._tagFieldsGenerator.getTagSelectField({ key: 'tags' }, { ordering: 'name' }),
      this._cameraFieldsGeneratorService.getCamerasCoreField({ key: 'appliance' }),
      {
        key: 'isAiEnabled',
        type: 'select',
        templateOptions: {
          label: t('common.ai'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('common.enabled'), value: true },
            { label: t('cameras.ai.notEnabled'), value: false },
          ],
        },
      },
      {
        key: 'starred',
        type: 'select',
        templateOptions: {
          label: t('common.favorited'),
          floatLabel: 'always',
          placeholder: t('common.none'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('common.favorited'), value: true },
            { label: t('cameras.unfavorited'), value: false },
          ],
        },
      },
      {
        key: 'isFususCamera',
        type: 'select',
        templateOptions: {
          label: t('cameras.filters.cameraType'),
          floatLabel: 'always',
          placeholder: t('common.all'),
          options: [
            { label: t('common.all'), value: null },
            { label: t('cameras.type.fususCamera'), value: true },
          ],
        },
      },
      {
        key: 'exclude_child_cameras',
        type: 'toggle',
        templateOptions: {
          label: t('cameras.filters.excludeChildCameras'),
        },
      },
    ];

    if (fields?.length) {
      const selectedFields = [];
      fields.forEach((fieldName) => {
        formFields.forEach((field) => {
          if (fieldName === field.key.toString()) {
            selectedFields.push(field);
          }
        });
      });
      return selectedFields;
    }
    return formFields;
  }
}
